import { useState, useEffect, useRef, useCallback } from 'react'
import { CheckCircle, AlertTriangle, RefreshCw } from 'lucide-react'
import { getGoogleOAuthUrl } from '../../services/registrationApi'

interface GoogleConnectButtonProps {
  // Whether Google has already been connected for this session
  isConnected?: boolean
  // Called with the Fernet-encrypted credential blob once the OAuth popup
  // completes. The blob is opaque to the frontend — it is forwarded to the
  // backend as-is and never decrypted client-side.
  onConnect: (encryptedCredentials: string) => void
}

type ConnectionState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'connected'; email: string }
  | { status: 'warning' }
  | { status: 'error'; message: string }

export default function GoogleConnectButton({
  isConnected,
  onConnect,
}: GoogleConnectButtonProps) {
  const [state, setState] = useState<ConnectionState>(
    isConnected ? { status: 'connected', email: '' } : { status: 'idle' },
  )

  const popupRef = useRef<Window | null>(null)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const messageReceivedRef = useRef(false)

  const clearPoll = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }, [])

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.data?.type === 'google_oauth_success') {
        messageReceivedRef.current = true
        clearPoll()
        popupRef.current?.close()
        popupRef.current = null
        onConnect(event.data.encrypted_credentials as string)
        setState({ status: 'connected', email: event.data.email as string })
      } else if (event.data?.type === 'google_oauth_error') {
        messageReceivedRef.current = true
        clearPoll()
        popupRef.current?.close()
        popupRef.current = null
        const errorCode = event.data.error as string
        const errorMessages: Record<string, string> = {
          missing_gbp_scope: 'Google Business Profile access was not granted. Please go to myaccount.google.com/permissions, remove this app, then click Retry to reconnect and approve all permissions.',
          token_exchange_failed: 'Google sign-in failed. Please try again.',
          encryption_failed: 'An internal error occurred. Please try again.',
        }
        setState({
          status: 'error',
          message: errorMessages[errorCode] ?? errorCode ?? 'An error occurred during Google sign-in.',
        })
      }
    },
    [onConnect, clearPoll],
  )

  useEffect(() => {
    const channel = new BroadcastChannel('google_oauth')
    channel.onmessage = (event: MessageEvent) => handleMessage(event)
    return () => {
      channel.close()
      clearPoll()
    }
  }, [handleMessage, clearPoll])

  const handleConnect = async () => {
    setState({ status: 'loading' })
    messageReceivedRef.current = false

    let oauthUrl: string
    try {
      const result = await getGoogleOAuthUrl()
      oauthUrl = result.url
    } catch {
      setState({ status: 'error', message: 'Failed to start Google sign-in. Please try again.' })
      return
    }

    const popup = window.open(oauthUrl, '_blank', 'popup=true,width=600,height=700')
    if (!popup) {
      setState({
        status: 'error',
        message: 'Popup was blocked. Please allow popups for this site and try again.',
      })
      return
    }

    popupRef.current = popup

    pollIntervalRef.current = setInterval(() => {
      if (popup.closed) {
        clearPoll()
        popupRef.current = null
        if (!messageReceivedRef.current) {
          setState({ status: 'warning' })
        }
      }
    }, 500)
  }

  // Connected state
  if (state.status === 'connected') {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[rgba(74,222,128,0.4)] bg-[rgba(74,222,128,0.08)] px-4 py-3">
        <CheckCircle className="h-5 w-5 shrink-0 text-rd-accent" aria-hidden="true" />
        <span className="text-sm font-medium text-rd-accent">
          Connected{state.email ? `: ${state.email}` : ''}
        </span>
      </div>
    )
  }

  // Warning state — popup closed without auth
  if (state.status === 'warning') {
    return (
      <div className="space-y-3">
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-[rgba(251,191,36,0.3)] bg-[rgba(251,191,36,0.06)] px-4 py-3"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-yellow-400" aria-hidden="true" />
          <p className="text-sm text-yellow-400">
            Google connection required for automatic review fetching. You can connect later from the
            dashboard.
          </p>
        </div>
        <button
          type="button"
          onClick={handleConnect}
          className="reg-btn-secondary flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Retry Google Connection
        </button>
      </div>
    )
  }

  // Error state
  if (state.status === 'error') {
    return (
      <div className="space-y-3">
        <div
          role="alert"
          className="rounded-lg border border-red-500/30 bg-[rgba(239,68,68,0.08)] px-4 py-3 text-sm text-red-400"
        >
          {state.message}
        </div>
        <button
          type="button"
          onClick={handleConnect}
          className="reg-btn-secondary flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Retry
        </button>
      </div>
    )
  }

  // Idle / loading state
  const isLoading = state.status === 'loading'

  return (
    <button
      type="button"
      onClick={handleConnect}
      disabled={isLoading}
      aria-busy={isLoading}
      className="reg-btn-secondary"
    >
      {isLoading ? (
        <>
          <svg
            aria-hidden="true"
            className="h-4 w-4 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          Connecting…
        </>
      ) : (
        <>
          {/* Google "G" icon */}
          <svg
            aria-hidden="true"
            className="h-4 w-4"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Connect Google Business Profile
        </>
      )}
    </button>
  )
}
